import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useSettings } from "@/contexts/SettingsContext";
import { Plus, Pencil, Trash2, AlertTriangle, FileText, Upload, Download, X, Eye, RefreshCw, FileSpreadsheet, Ban, ShieldCheck, CreditCard, Building2, Banknote, ArrowLeftRight } from "lucide-react";

const empty = {
  customer_name: "", customer_phone: "", customer_tc: "",
  product_key: "", policy_number: "", start_date: "",
  end_date: "", premium: 0, notes: "",
  company_id: "", payment_method: "", payment_card_desc: "",
  dynamic_fields: {},
};

const PAY_METHODS = [
  { v: "nakit", l: "Nakit", icon: Banknote },
  { v: "havale", l: "Havale/EFT", icon: ArrowLeftRight },
  { v: "kart", l: "Kredi Kartı", icon: CreditCard },
];

export default function PoliciesPage() {
  const { settings } = useSettings();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(null);
  const fileInputs = useRef({});
  const [fullSettings, setFullSettings] = useState(null);

  const customFields = useMemo(() => {
    // Prefer admin-scoped settings (loaded here) so staff also see published defaults from public settings if available.
    const cf = fullSettings?.policy_custom_fields;
    if (Array.isArray(cf)) return cf;
    return [];
  }, [fullSettings]);
  const companies = useMemo(() => Array.isArray(settings?.insurance_companies) ? settings.insurance_companies : [], [settings]);

  const load = () => api.get("/policies").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    // Load full settings for custom fields (works for both admin & staff via public endpoint fallback)
    api.get("/settings/full").then((r) => setFullSettings(r.data)).catch(() => {
      api.get("/settings").then((r) => setFullSettings(r.data)).catch(() => {});
    });
  }, []);

  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview?.url]);

  const openNew = () => { setEditing(null); setForm({ ...empty, dynamic_fields: {} }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      customer_name: p.customer_name || "",
      customer_phone: p.customer_phone || "",
      customer_tc: p.customer_tc || "",
      product_key: p.product_key || "",
      policy_number: p.policy_number || "",
      start_date: p.start_date || "",
      end_date: p.end_date || "",
      premium: p.premium || 0,
      notes: p.notes || "",
      company_id: p.company_id || "",
      payment_method: p.payment_method || "",
      payment_card_desc: p.payment_card_desc || "",
      dynamic_fields: p.dynamic_fields || {},
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form };
      // Normalize empty strings for optional selects
      if (!payload.company_id) payload.company_id = null;
      if (!payload.payment_method) payload.payment_method = null;
      if (payload.payment_method !== "kart") payload.payment_card_desc = null;
      if (editing) {
        await api.put(`/policies/${editing.id}`, payload);
        toast.success("Poliçe güncellendi");
      } else {
        await api.post("/policies", payload);
        toast.success("Poliçe eklendi");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Poliçeyi silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/policies/${id}`); toast.success("Poliçe silindi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadPdf = async (policyId, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Dosya 15 MB'ı geçemez"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/policies/${policyId}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF yüklendi"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const fetchPdfBlob = async (policyId) => {
    const token = localStorage.getItem("asg_token");
    const res = await fetch(`${API}/policies/${policyId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("PDF alınamadı");
    return await res.blob();
  };

  const openPreview = async (policyId, fname) => {
    try { const blob = await fetchPdfBlob(policyId); setPreview({ id: policyId, name: fname || "police.pdf", url: URL.createObjectURL(blob) }); }
    catch (e) { toast.error(e.message); }
  };
  const downloadPdf = async (policyId, fname) => {
    try {
      const blob = await fetchPdfBlob(policyId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = fname || "police.pdf"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };
  const removePdf = async (policyId) => {
    if (!confirm("Yüklenmiş PDF silinsin mi?")) return;
    try { await api.delete(`/policies/${policyId}/pdf`); toast.success("PDF silindi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const renewPolicy = async (policyId) => {
    if (!confirm("Bu poliçe için yenileme talebi oluşturulsun mu?")) return;
    try { await api.post(`/policies/${policyId}/renew`); toast.success("Yenileme talebi oluşturuldu"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const openCancel = (p) => { setCancelDialog(p); setCancelReason(p.cancellation_reason || ""); };
  const applyCancel = async () => {
    if (!cancelReason.trim()) { toast.error("İptal nedeni gerekli"); return; }
    try {
      await api.put(`/policies/${cancelDialog.id}`, { is_cancelled: true, cancellation_reason: cancelReason.trim() });
      toast.success("Poliçe iptal edildi");
      setCancelDialog(null); setCancelReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reactivate = async (policyId) => {
    if (!confirm("Poliçe tekrar aktif hale getirilsin mi?")) return;
    try { await api.put(`/policies/${policyId}`, { is_cancelled: false }); toast.success("Poliçe yeniden aktif"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDyn = (name, v) => setForm((f) => ({ ...f, dynamic_fields: { ...(f.dynamic_fields || {}), [name]: v } }));

  const exportFile = async (format) => {
    try {
      const token = localStorage.getItem("asg_token");
      const res = await fetch(`${API}/exports/policies?format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Dışa aktarma başarısız");
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `policeler-${new Date().toISOString().slice(0,10)}.${format === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("İndirildi");
    } catch (e) { toast.error(e.message); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "";

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl">Poliçeler</h1>
          <p className="text-[color:var(--asg-muted)] text-sm mt-1">Poliçeleri yönetin, tahsilat türü ve şirket bilgilerini takip edin, PDF ekleyip önizleyin.</p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <Button onClick={openNew} className="asg-btn-primary rounded-full border-0" data-testid="policy-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Yeni Poliçe
          </Button>
          <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="policies-export-xlsx" className="border-[color:var(--asg-border)] bg-transparent">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={() => exportFile("csv")} data-testid="policies-export-csv" className="border-[color:var(--asg-border)] bg-transparent">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
        </div>
      </div>

      <div className="asg-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--asg-surface)] text-[color:var(--asg-muted)] uppercase tracking-widest text-xs">
              <tr>
                <th className="text-left p-4">Poliçe No</th>
                <th className="text-left p-4">Müşteri</th>
                <th className="text-left p-4">Ürün</th>
                <th className="text-left p-4">Şirket</th>
                <th className="text-left p-4">Tahsilat</th>
                <th className="text-left p-4">Başlangıç</th>
                <th className="text-left p-4">Bitiş</th>
                <th className="text-left p-4">PDF</th>
                <th className="text-left p-4">Kalan</th>
                <th className="text-right p-4">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="10" className="p-8 text-center text-[color:var(--asg-muted)]">Henüz poliçe yok</td></tr>
              )}
              {rows.map((p) => {
                const dl = p.days_left;
                const cancelled = !!p.is_cancelled;
                const critical = !cancelled && dl !== null && dl <= 3;
                const canRenew = !cancelled && dl !== null && dl <= 15 && !p.renewal_request_id;
                const payMethod = PAY_METHODS.find((m) => m.v === p.payment_method);
                return (
                  <tr key={p.id} className={`border-t border-[color:var(--asg-border)] ${cancelled ? "opacity-60 bg-zinc-100/40" : critical ? "bg-amber-50" : ""}`} data-testid={`policy-row-${p.id}`}>
                    <td className={`p-4 font-mono ${cancelled ? "line-through text-[color:var(--asg-muted)]" : "text-[color:var(--asg-text)]"}`}>{p.policy_number}</td>
                    <td className="p-4 text-[color:var(--asg-text)]">{p.customer_name}<div className="text-xs text-[color:var(--asg-muted)]">{p.customer_phone}</div></td>
                    <td className="p-4 text-[color:var(--asg-muted)]">{p.product_title}</td>
                    <td className="p-4 text-[color:var(--asg-muted)]">{companyName(p.company_id) || <span className="text-xs opacity-60">—</span>}</td>
                    <td className="p-4">
                      {payMethod ? (
                        <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--asg-primary-dark)" }} data-testid={`policy-pay-${p.id}`}>
                          <payMethod.icon className="w-3.5 h-3.5" /> {payMethod.l}
                          {p.payment_card_desc && <span className="text-[10px] text-[color:var(--asg-muted)] ml-1">({p.payment_card_desc})</span>}
                        </span>
                      ) : <span className="text-xs opacity-60">—</span>}
                    </td>
                    <td className="p-4 text-[color:var(--asg-muted)]">{p.start_date}</td>
                    <td className="p-4 text-[color:var(--asg-muted)]">{p.end_date}</td>
                    <td className="p-4">
                      {p.pdf_name ? (
                        <div className="flex items-center gap-2">
                          <button onClick={() => openPreview(p.id, p.pdf_name)} className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 text-xs" data-testid={`policy-pdf-preview-${p.id}`}><Eye className="w-3.5 h-3.5" /> Önizle</button>
                          <button onClick={() => downloadPdf(p.id, p.pdf_name)} className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 text-xs" data-testid={`policy-pdf-download-${p.id}`}><Download className="w-3.5 h-3.5" /> İndir</button>
                          <button onClick={() => removePdf(p.id)} className="text-zinc-400 hover:text-red-500" data-testid={`policy-pdf-remove-${p.id}`}><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <>
                          <input ref={(el) => { fileInputs.current[p.id] = el; }} type="file" accept="application/pdf" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdf(p.id, f); e.target.value = ""; }} />
                          <button onClick={() => fileInputs.current[p.id]?.click()} className="inline-flex items-center gap-1 text-[color:var(--asg-muted)] hover:text-[color:var(--asg-text)] text-xs" data-testid={`policy-pdf-upload-${p.id}`}>
                            <Upload className="w-3.5 h-3.5" /> Yükle
                          </button>
                        </>
                      )}
                    </td>
                    <td className="p-4">
                      {cancelled ? (
                        <span className="text-[color:var(--asg-muted)] text-xs flex items-center gap-1" data-testid={`policy-cancelled-badge-${p.id}`}>
                          <Ban className="w-3 h-3" /> İptal
                        </span>
                      ) : dl === null ? <span className="text-[color:var(--asg-muted)]">—</span> :
                        dl < 0 ? <span className="text-red-500 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {Math.abs(dl)} gün önce bitti</span> :
                        critical ? <span className="text-amber-600 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {dl} gün</span> :
                        <span className="text-emerald-600">{dl} gün</span>}
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      {p.renewal_request_id && !cancelled && (
                        <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 mr-2">Yenileme Açıldı</span>
                      )}
                      {canRenew && (
                        <button onClick={() => renewPolicy(p.id)} className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 mr-3" title="Yenileme talebi oluştur" data-testid={`policy-renew-${p.id}`}>
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      {cancelled ? (
                        <button onClick={() => reactivate(p.id)} className="text-emerald-600 hover:text-emerald-700 mr-3" title="Yeniden aktif et" data-testid={`policy-reactivate-${p.id}`}>
                          <ShieldCheck className="w-4 h-4" />
                        </button>
                      ) : (
                        <button onClick={() => openCancel(p)} className="text-zinc-400 hover:text-red-500 mr-3" title="Poliçeyi iptal et" data-testid={`policy-cancel-${p.id}`}>
                          <Ban className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => openEdit(p)} className="text-zinc-500 hover:text-[color:var(--asg-primary-dark)] mr-2" data-testid={`policy-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(p.id)} className="text-zinc-400 hover:text-red-500" data-testid={`policy-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[color:var(--asg-border)] text-[color:var(--asg-text)] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="policy-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Poliçe Düzenle" : "Yeni Poliçe"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2"><Label>Müşteri Ad Soyad</Label>
              <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" data-testid="policy-form-name" />
            </div>
            <div className="grid gap-2"><Label>Telefon</Label>
              <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" />
            </div>
            <div className="grid gap-2"><Label>T.C. Kimlik</Label>
              <Input value={form.customer_tc} onChange={(e) => set("customer_tc", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" />
            </div>
            <div className="grid gap-2"><Label>Poliçe No</Label>
              <Input value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" data-testid="policy-form-number" />
            </div>
            <div className="grid gap-2"><Label>Ürün</Label>
              <Select value={form.product_key} onValueChange={(v) => set("product_key", v)}>
                <SelectTrigger className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-white border-[color:var(--asg-border)]">
                  {products.map((p) => <SelectItem key={p.key} value={p.key}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2"><Label className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Sigorta Şirketi</Label>
              <Select value={form.company_id || ""} onValueChange={(v) => set("company_id", v)}>
                <SelectTrigger className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" data-testid="policy-form-company">
                  <SelectValue placeholder={companies.length ? "Şirket seçin" : "Şirket eklenmemiş"} />
                </SelectTrigger>
                <SelectContent className="bg-white border-[color:var(--asg-border)]">
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2"><Label>Prim (₺)</Label>
              <Input type="number" value={form.premium} onChange={(e) => set("premium", parseFloat(e.target.value) || 0)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" />
            </div>
            <div className="grid gap-2"><Label>Başlangıç Tarihi</Label>
              <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" />
            </div>
            <div className="grid gap-2"><Label>Bitiş Tarihi</Label>
              <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" data-testid="policy-form-end" />
            </div>
          </div>

          {/* Payment method */}
          <div className="mt-6 rounded-xl border border-[color:var(--asg-border)] p-4 bg-[color:var(--asg-surface)]">
            <Label className="text-sm font-heading">Tahsilat Bilgisi</Label>
            <div className="grid grid-cols-3 gap-2 mt-3">
              {PAY_METHODS.map((m) => {
                const active = form.payment_method === m.v;
                return (
                  <button key={m.v} type="button" onClick={() => set("payment_method", active ? "" : m.v)}
                    className={`flex items-center justify-center gap-2 h-11 rounded-lg text-sm transition border ${active ? "border-[color:var(--asg-primary)] bg-white shadow-sm" : "border-[color:var(--asg-border)] bg-white/60 hover:bg-white"}`}
                    data-testid={`policy-form-pay-${m.v}`}>
                    <m.icon className={`w-4 h-4 ${active ? "asg-primary" : ""}`} /> <span className={active ? "font-medium" : ""}>{m.l}</span>
                  </button>
                );
              })}
            </div>
            {form.payment_method === "kart" && (
              <div className="mt-3 grid gap-2">
                <Label className="text-xs">Kart Açıklaması (İlk 4 ve Son 4 rakam)</Label>
                <Input value={form.payment_card_desc || ""} onChange={(e) => set("payment_card_desc", e.target.value)}
                  placeholder="Örn: 5432 **** **** 8765"
                  className="bg-white border-[color:var(--asg-border)] font-mono" data-testid="policy-form-card-desc" />
                <p className="text-[10px] text-[color:var(--asg-muted)]">Güvenlik için ortadaki 8 rakamı yıldız olarak bırakın. Örn: 4532 **** **** 1245</p>
              </div>
            )}
          </div>

          {/* Dynamic custom fields from settings.policy_custom_fields */}
          {customFields.length > 0 && (
            <div className="mt-4 rounded-xl border border-[color:var(--asg-border)] p-4 bg-[color:var(--asg-surface)]">
              <Label className="text-sm font-heading">Özel Alanlar</Label>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {customFields.map((f) => (
                  <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2 grid gap-1" : "grid gap-1"}>
                    <Label className="text-xs text-[color:var(--asg-muted)]">
                      {f.label} {f.required && <span className="asg-primary">*</span>}
                    </Label>
                    {f.type === "textarea" ? (
                      <Textarea value={form.dynamic_fields?.[f.name] || ""} onChange={(e) => setDyn(f.name, e.target.value)}
                        className="bg-white border-[color:var(--asg-border)]" data-testid={`policy-dyn-${f.name}`} />
                    ) : (
                      <Input type={f.type || "text"} value={form.dynamic_fields?.[f.name] || ""}
                        onChange={(e) => setDyn(f.name, e.target.value)}
                        className="bg-white border-[color:var(--asg-border)]" data-testid={`policy-dyn-${f.name}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-2"><Label>Notlar</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} className="asg-btn-primary rounded-full border-0" data-testid="policy-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={(o) => { if (!o) { setCancelDialog(null); setCancelReason(""); } }}>
        <DialogContent className="bg-white border-[color:var(--asg-border)] text-[color:var(--asg-text)] max-w-md" data-testid="policy-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2"><Ban className="w-5 h-5 asg-primary" /> Poliçeyi İptal Et</DialogTitle>
            <DialogDescription className="text-[color:var(--asg-muted)]">
              {cancelDialog?.customer_name} — {cancelDialog?.policy_number}. İptal edilen poliçe artık uyarı vermez.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Label>İptal Nedeni <span className="asg-primary">*</span></Label>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Örn: Müşteri aracını sattı"
              className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)]" data-testid="policy-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelDialog(null)}>Vazgeç</Button>
            <Button onClick={applyCancel} className="asg-btn-primary rounded-full border-0" data-testid="policy-cancel-confirm">İptal Et</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="bg-white border-[color:var(--asg-border)] max-w-5xl h-[85vh] p-0 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--asg-border)]">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 asg-primary shrink-0" />
              <div className="truncate font-heading">{preview?.name}</div>
            </div>
            <div className="flex items-center gap-2">
              {preview && (
                <Button onClick={() => downloadPdf(preview.id, preview.name)} variant="outline" size="sm"
                  className="rounded-full border-[color:var(--asg-border)] bg-transparent" data-testid="pdf-preview-download">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> İndir
                </Button>
              )}
            </div>
          </div>
          <div className="flex-1 bg-zinc-100">
            {preview?.url && <iframe title="Poliçe PDF" src={preview.url} className="w-full h-full border-0" data-testid="pdf-preview-iframe" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
